import { addDays, endOfDay, format, startOfDay, subYears } from 'date-fns';
import NumberFormat from 'react-number-format';
import { useLocation } from 'react-router-dom';
import { TransactionItemSummaryV2Dto, TransSearchField, ValidTransactionStatusEnum } from '@inosio/payments-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Card, Grid, IconButton, Link, Snackbar, Stack, TableContainer, TablePagination, Tooltip, Typography } from '@mui/material';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getTransactionHistoryDetails, selectPMPaymentState, selectTransactionHistoryDetails, selectTransHistoryDetailsCurrentPage } from './sagas/pmTransactionsSlice';
import { convertCentsToDollars } from '../../shared/util/converters';
import TransactionDetails from './components/TransactionDetails';
import ExportDataModal, { IFileExportDetails } from '../../components/ExportDataModal';
import ExportIcon from '../../icons/ExportIcon.svg';
import SpinnerWithNoConfirmation from '../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../icons/Badrequesterror.svg';
import Error500 from '../../icons/505Error.svg';
import { Fragment, createRef, useEffect, useMemo, useState } from 'react';
import SearchableTableHeader from '../shared/SearchableTableHeader';
import { IsoDateRange } from '../shared/CalendarModal.V2';
import { GetExtendedTransactionStatus } from '../shared/util/helpers';

export function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const optionValues = [
  { key: TransSearchField.PaymentId, value: 'Payment ID' },
  { key: TransSearchField.PayerName, value: 'Name' },
  { key: TransSearchField.PropertyName, value: 'Property' },
  { key: TransSearchField.Amount, value: 'Amount' },
  { key: TransSearchField.Status, value: 'Status' },
];

const useStyles = makeStyles({
  gridContainer: {
    background: '#FFFFFF',
    borderRadius: '24px',
  },
  dialog: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogbox: {
    borderRadius: '20',
    borderStyle: 'solid',
  },
});

const Transactions = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const transaction: TransactionItemSummaryV2Dto = {} as TransactionItemSummaryV2Dto;
  const defaultStartDate = startOfDay(addDays(subYears(new Date(), 1), 1)).toISOString();
  const defaultEndDate = endOfDay(new Date()).toISOString();

  const [searchDateRange, setSearchDateRange] = useState<IsoDateRange | undefined>();
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [rangeSize, setRangeSize] = useState(100);
  const [searchField, setsearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const searchValueInputRef = createRef<HTMLInputElement>();
  const columnsSelectRef = createRef<HTMLSelectElement>();
  const [showExportModal, setShowExportModal] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedRow, setSelectedRow] = useState(transaction);
  const [currentPage, setCurrentPage] = useState(0);
  const [transactionAmountError, setTransactionAmountError] = useState('');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  let transactionHistoryData = useSelector(selectTransactionHistoryDetails);
  let transHistoryDataBook = useSelector(selectTransHistoryDetailsCurrentPage);
  const transactionState = useSelector(selectPMPaymentState);

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!transHistoryDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };
  const handleCloseDetails = (event: object, reason: string) => setOpenDetails(false);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  const formatSearchAmount = (majorUnitAmount: string) => {
    const formattedAmount = parseFloat(majorUnitAmount.replace(/,/g, '')) * 100;
    if (majorUnitAmount.match(/[a-z]/i)) {
      setTransactionAmountError('Amount should be a valid number');
      setSnackBarOpen(true);
      return false;
    }
    if (majorUnitAmount === null || majorUnitAmount === '' || isNaN(formattedAmount)) {
      return 0;
    }
    return formattedAmount;
  };

  const toggleShowExportModal = () => {
    setShowExportModal(!showExportModal);
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const handleSearches = (rangeSize: number, startIndex: number, dateRange?: IsoDateRange | undefined, searchField?: string, searchValue?: string, exportDetails?: IFileExportDetails) => {
    if (!searchValue) searchField = undefined;

    const transSearchDto = {
      EndDate: dateRange?.[1] || undefined,
      StartDate: dateRange?.[0] || undefined,
      RangeSize: rangeSize,
      StartIndex: startIndex,
      SearchField: searchField || undefined,
      SearchValue: searchValue || undefined,
      exportAs: exportDetails?.exportAs,
      fileName: exportDetails?.fileName,
    };

    if (searchValue) {
      if (searchField === 'Amount') {
        transSearchDto.SearchValue = formatSearchAmount(searchValue).toString();
      }
    } else {
      transSearchDto.EndDate = transSearchDto.EndDate || defaultEndDate;
      transSearchDto.StartDate = transSearchDto.StartDate || defaultStartDate;
    }

    dispatch(getTransactionHistoryDetails(transSearchDto));
  };

  useEffect(() => {
    handleSearches(rangeSize, recordStartIndex, searchDateRange, searchField, searchValue);
  }, [dispatch, rangeSize, recordStartIndex, searchDateRange, searchField, searchValue]);

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 'calc( 100vh - 144px )', pb: '64px' }} className={classes.gridContainer} style={{ position: 'relative' }}>
        {
          <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
            <Alert onClose={handleAlertClose} severity="error">
              {transactionAmountError}
            </Alert>
          </Snackbar>
        }
        <Fragment>
          <Box sx={{ display: 'flex', p: 1, pb: 2, justifyContent: 'space-between', alignItems: 'start', '& .MuiTextField-root': { m: 1, width: '25ch' } }}>
            <SearchableTableHeader
              isDateOnly={false}
              initialSearchValue={searchValue}
              searchParam={searchField || optionValues[1].key}
              searchValuePlaceholder="Type Here To Search"
              dropDownOptionValues={optionValues}
              executeSearch={(searchField?: string | undefined, searchValue?: string | undefined, dateRange?: IsoDateRange | undefined) => {
                setsearchField(searchField || '');
                setSearchValue(searchValue || '');
                setSearchDateRange(dateRange);
                setRecordStartIndex(0); //reset start index
                setCurrentPage(0); //go back to page 1
                //we'll rely on our useEffect to invoke the search, as to not have the code in too many places
              }}
              searchValueInputRef={searchValueInputRef} //!WTF this do?
              columnsSelectRef={columnsSelectRef} //!WTF this do?
            />
            <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: '5px' }}>
              <IconButton aria-label="export" onClick={toggleShowExportModal}>
                <img src={ExportIcon} alt="export icon" width="22px" height="22px" />
              </IconButton>
            </Box>
          </Box>
          {String(transactionState.errorMessage.statusCode)[0] === '4' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" pb={3} mt={3}>
                  <img src={BadRequestIcon} alt="400" />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {transactionState.errorMessage.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {transactionState.errorMessage.message.length === 0 ? '' : transactionState.errorMessage.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : String(transactionState.errorMessage.statusCode)[0] === '5' ? (
            <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
              <Grid item display="flex" flexDirection="column" alignItems="center">
                <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                  <img src={Error500} alt="500" style={{ height: '35vh' }} />
                </Grid>
                <Grid item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '40px',
                      lineHeight: '23px',
                      color: '#2E82AF',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {transactionState.errorMessage.statusCode}
                  </Typography>
                </Grid>
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
                <Grid item justifyContent="center" mt={2} xs={8}>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    {transactionState.errorMessage.message.length === 0 ? '' : transactionState.errorMessage.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : transactionState.isFetchingTransaction ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              style={{
                height: '1100px',
              }}
            >
              <SpinnerWithNoConfirmation />
            </Stack>
          ) : (
            <TableContainer style={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell id="subTitle">Date</TableCell>
                    <TableCell id="subTitle">Payment ID</TableCell>
                    <TableCell id="subTitle">Name</TableCell>
                    <TableCell id="subTitle">Property</TableCell>
                    <TableCell id="subTitle">Amount</TableCell>
                    <TableCell id="subTitle">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ left: '28px' }}>
                  {transHistoryDataBook?.[currentPage] &&
                    transHistoryDataBook?.[currentPage].Rows?.map((eachRow, i) => {
                      let statusText: string = eachRow.Status;
                      let statusToolTip = GetExtendedTransactionStatus(eachRow) || '';
                      if (['Approved', 'Issued', 'Requested', 'Denied'].includes(eachRow.RefundDetails?.Status || '') && eachRow.Status === ValidTransactionStatusEnum.Completed) {
                        statusText = `Refund ${eachRow.RefundDetails?.Status}`;
                      }

                      return (
                        <TableRow key={eachRow.TransactionUuid}>
                          <TableCell id="content">{format(new Date(eachRow?.TransactionDate), 'MM/dd/yyyy')}</TableCell>
                          <TableCell id="content">
                            <Link
                              href="#"
                              underline="hover"
                              color="inherit"
                              onClick={() => {
                                setOpenDetails(true);
                                setSelectedRow(eachRow);
                              }}
                            >
                              {eachRow.TransactionUuid}
                            </Link>
                          </TableCell>
                          <TableCell id="content">{eachRow.PayerName}</TableCell>
                          <TableCell id="content">{eachRow.PayeeName}</TableCell>
                          <TableCell id="content">
                            <NumberFormat
                              thousandsGroupStyle="thousand"
                              value={convertCentsToDollars(eachRow.Amount)}
                              prefix="$"
                              decimalSeparator="."
                              displayType="text"
                              type="text"
                              thousandSeparator={true}
                              allowNegative={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </TableCell>
                          <TableCell id="content">
                            <Tooltip title={statusToolTip} placement="right">
                              <span>{statusText}</span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[50, 100, 200]}
            count={transactionHistoryData.TotalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rangeSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ position: 'absolute', bottom: 10, right: 10 }}
          />
          {selectedRow && Object.keys(selectedRow).length !== 0 ? (
            <TransactionDetails
              open={openDetails}
              closeCallback={handleCloseDetails}
              deviceWidth={window.innerWidth}
              paymentID={selectedRow.TransactionUuid}
              transactionDate={selectedRow?.TransactionDate}
              transactionDateShort={format(new Date(selectedRow?.TransactionDate), 'MM/dd/yy')}
              refundStatus={selectedRow?.RefundDetails?.Status}
              transactionStatus={selectedRow?.Status}
              refreshTransaction={() => {
                handleSearches(rangeSize, recordStartIndex, searchDateRange, searchField, searchValue);
              }}
              refundReceipt={false}
            />
          ) : (
            <></>
          )}
          <ExportDataModal
            name={'Transaction'}
            visible={showExportModal}
            onExport={(fileType, fileName) => handleSearches(0, 0, searchDateRange, searchField, searchValue, { exportAs: fileType, fileName })}
            toggleShowExportModal={toggleShowExportModal}
          />
        </Fragment>
      </Card>
    </Grid>
  );
};

export default Transactions;
